import { getNestedOrFloatingElementById, getNestedSubelementsById } from '@fs/zion-sls'
import { getPersonsFromIds } from '../../../../../components/generic/shared/utils/movePersonsUtils'

/**
 * Handles the drag start event for a person element.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.active - The active draggable element.
 * @param {Object} params.formMethods - The form methods for handling form state.
 * @param {Array} params.images - The array of zion Sls image objects.
 * @param {Array} params.allRecords - The array of all record objects.
 */
export const onDragStartHandler = ({ active, formMethods, images, allRecords }) => {
  const [selectedPersonsIds, isMultiSelect] = formMethods.getValues(['selectedPersonsIds', 'isMultiSelect'])
  const draggedElementId = active?.id
  const isDraggedElementSelected = selectedPersonsIds.includes(draggedElementId)

  if (!isDraggedElementSelected) {
    formMethods.setValue('selectedPersonsIds', [draggedElementId, ...selectedPersonsIds])
  }

  const dragPerson = getNestedOrFloatingElementById(images, draggedElementId)
  const newDragInfo = {
    isDragging: true,
    person: dragPerson,
    record: getNestedSubelementsById(allRecords, dragPerson?.recordId)?.[0],
  }
  formMethods.setValue('dragInfo', newDragInfo)

  if (!isMultiSelect) formMethods.setValue('selectedPersonsIds', [dragPerson?.id])
}

/**
 * Handles the drag end event for dragging persons between records.
 *
 * @param {Object} params - The parameters for the handler.
 * @param {Object} params.active - The active draggable item.
 * @param {Object} params.over - The droppable area where the item is dropped.
 * @param {Object} params.formMethods - Methods for handling form state.
 * @param {Array} params.images - The list of images.
 * @param {Object} params.newRecordOverlay - The overlay for creating a new record. From the useOverlay hook
 * @param {string} params.currentImageArk - The ARK identifier for the current image.
 * @param {Function} params.savePersonInOtherRecord - Function to save persons in another record.
 */
export const onDragEndHandler = ({
  active,
  over,
  formMethods,
  images,
  newRecordOverlay,
  currentImageArk,
  savePersonInOtherRecord,
}) => {
  const selectedPersonsIds = formMethods.getValues('selectedPersonsIds')
  const draggedPersons = getPersonsFromIds({ images, personsIds: selectedPersonsIds })

  const activeRecordId = active?.data?.current?.record?.id
  const destinationRecordId = over?.id
  const allSelectedPersonsFromSameRecord = draggedPersons.every((person) => person?.recordId === activeRecordId)

  // If the selected persons are from the same record and the destination is the same record, do nothing
  if (allSelectedPersonsFromSameRecord && activeRecordId === destinationRecordId) {
    formMethods.setValue('dragInfo', null)
    formMethods.setValue('selectedPersonsIds', [])
    return
  }

  // If the destination is a new record, open the new record dialog
  if (destinationRecordId === 'new_record') {
    newRecordOverlay.handleClick()
  } else {
    // If the destination is an existing record, save the persons in the destination record
    savePersonInOtherRecord(destinationRecordId, draggedPersons, currentImageArk)
  }

  formMethods.setValue('dragInfo', null)
}

/**
 * Handles the drag cancel event by resetting the drag information in the form.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.formMethods - The form methods object.
 */
export const onDragCancelHandler = ({ formMethods }) => {
  formMethods.setValue('dragInfo', null)
}
